import { useQuery, UseQueryResult } from '@tanstack/react-query';
import deploymentService from '../services/DeploymentService';
import { DeploymentDetailResponse } from '../models/Deployment';

const POLLING_INTERVAL_MS = 5_000;

export const deploymentPollingQueryKey = (projectId: string | null) =>
  ['deployment', 'latest', projectId] as const;

interface UseDeploymentPollingOptions {
  /** 폴링을 활성화할지 여부. 일반적으로 activeDeployment.status === 'in_progress' 일 때 true. */
  enabled: boolean;
}

/**
 * 특정 현장의 최신 배포 상태를 폴링하는 훅.
 *
 * 동작 규칙:
 * - `enabled` + `projectId` 가 있을 때만 쿼리 실행
 * - 응답 status 가 'in_progress' 일 때만 5s 간격 refetch
 * - `document.hidden` 상태에서는 폴링 일시 중지 (refetchIntervalInBackground=false)
 * - status 가 'success' | 'failed' 로 전환되면 자동으로 폴링 중단
 *
 * 상위 호출자는 상태 전환 감지 후 ['deployment','active'] 및 현장 목록 쿼리를 invalidate 해야 함.
 */
export function useDeploymentPolling(
  projectId: string | null,
  options: UseDeploymentPollingOptions,
): UseQueryResult<DeploymentDetailResponse, Error> {
  const { enabled } = options;

  return useQuery<DeploymentDetailResponse, Error>({
    queryKey: deploymentPollingQueryKey(projectId),
    queryFn: () => deploymentService.getLatestDeployment(projectId as string),
    enabled: enabled && !!projectId,
    refetchInterval: (query) => {
      if (typeof document !== 'undefined' && document.hidden) {
        return false;
      }
      const status = query.state.data?.data?.status;
      return status === 'in_progress' ? POLLING_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
    retry: 3,
  });
}
